import {
  Component,
  HostListener,
  OnInit,
  CUSTOM_ELEMENTS_SCHEMA,
  AfterViewInit,
  OnDestroy
} from "@angular/core";
import { FlatTreeNode, TocItemTree, TreeNode } from "@orion2/models/tocitem.models";
import { Store } from "@viewer/core";
import { MaintFolderService } from "@viewer/toc-items/maint-folder-module/services/maint-folder.service";
import { TitleService } from "@viewer/header/title.service";
import { runInAction } from "mobx";
import { debounceTime, distinctUntilChanged, Subscription } from "rxjs";
import { MatTreeFlatDataSource, MatTreeModule } from "@angular/material/tree";
import { MatInputModule } from "@angular/material/input";
import {
  OrionCardPipe,
  OrionCardSource
} from "@viewer/shared-module/orion-card/pipe/orion-card.pipe";
import { HistorySource } from "@orion2/models/enums";
import { MatIconModule } from "@angular/material/icon";
import { SharedModule } from "@viewer/shared-module/shared.module";
import { MatButtonModule } from "@angular/material/button";
import { RouterModule } from "@angular/router";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatMenuModule } from "@angular/material/menu";
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
  ConfirmDialogResult
} from "libs/components/confirm-dialog/confirm-dialog.component";
import { TranslateService } from "@ngx-translate/core";
import { MatDialog } from "@angular/material/dialog";
import { FormControl, ReactiveFormsModule } from "@angular/forms";

@Component({
  standalone: true,
  selector: "o-maint-folder-list",
  imports: [
    MatTreeModule,
    MatIconModule,
    OrionCardPipe,
    SharedModule,
    MatButtonModule,
    RouterModule,
    MatCheckboxModule,
    MatButtonModule,
    MatInputModule,
    ReactiveFormsModule,
    MatMenuModule
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: "./maint-folder-list.component.html",
  styleUrl: "./maint-folder-list.component.scss"
})
export class MaintFolderListComponent implements OnInit, AfterViewInit, OnDestroy {
  public searchControl = new FormControl<string>("", { nonNullable: true });
  public readonly orionCardSource = OrionCardSource;
  public readonly historySource = HistorySource;
  public dataSource: MatTreeFlatDataSource<TreeNode, FlatTreeNode>;
  public unfilteredData: TreeNode[] = [];
  public selectMode = false;
  public displayReady = false;

  private subscription = new Subscription();
  private originFlatTree: FlatTreeNode[] = []; // unaltered flatTree used for export

  constructor(
    public maintFolderService: MaintFolderService,
    private titleService: TitleService,
    private store: Store,
    private translate: TranslateService,
    private dialog: MatDialog
  ) {}

  @HostListener("window:resize", ["$event"])
  onResize() {
    this.maintFolderService.setIndentation();
  }

  ngOnInit() {
    this.dataSource = new MatTreeFlatDataSource(
      this.maintFolderService.treeControl,
      this.maintFolderService.treeFlattener
    );
    this.titleService.setPageTitle("settings.maint.folders");
    this.maintFolderService.setIndentation();

    this.initDataSource();

    // unselect card on list and force combineLatest (toc.component)
    runInAction(() => {
      this.store.currentTocNode = {
        ...this.store.currentTocNode,
        unselected: true
      };
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public ngAfterViewInit(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(250), distinctUntilChanged())
      .subscribe((searchInput: string) => {
        return this.applyFilter(searchInput);
      });
  }

  public toggleExpansion(node: FlatTreeNode): void {
    this.maintFolderService.expansionModel.toggle(node.id);
    this.maintFolderService.treeControl.toggle(node);
  }

  public toggleSelection(event: Event, node: FlatTreeNode): void {
    event.preventDefault();
    event.stopPropagation();
    this.maintFolderService.selectionModel.toggle(node.id);
    const isCollapsed = !this.maintFolderService.expansionModel.isSelected(node.id);
    const isSelected = this.maintFolderService.selectionModel.isSelected(node.id);

    // SPEC: When we de/select a collapsed node, we want to de/select all its children.
    if (isCollapsed) {
      this.maintFolderService.toggleDescendantSelection(node, isSelected);
    }
  }

  public toggleSelectionAll(): void {
    this.selectMode = !this.selectMode;
    const func = this.selectMode ? "select" : "deselect";
    this.maintFolderService.treeControl.dataNodes.forEach((node: FlatTreeNode) =>
      this.maintFolderService.selectionModel[func](node.id)
    );
  }

  /**
   * Export a selection of nodes or all nodes if exportAll is set to true.
   * @param exportAll
   */
  public exportSelection(exportAll = false): void {
    const selectedNodes: FlatTreeNode[] = exportAll
      ? this.originFlatTree
      : this.maintFolderService.treeControl.dataNodes.filter((node: FlatTreeNode) =>
          this.maintFolderService.selectionModel.isSelected(node.id)
        );
    this.maintFolderService.exportNodes(selectedNodes);
  }

  /**
   * Deletes a selection of nodes or all nodes if deleteAll is set to true.
   * @param deleteAll By default the user selection is to be deleted. If set to true, all maintenance folders are
   * deleted.
   */
  public deleteSelection(deleteAll = false): void {
    const data: ConfirmDialogData = {
      message: [
        this.translate.instant(
          deleteAll ? "maint.folder.delete.all" : "maint.folder.delete.selection"
        )
      ],
      confirmLabels: [this.translate.instant("continue")],
      cancelLabels: [this.translate.instant("cancel")]
    };
    this.dialog
      .open(ConfirmDialogComponent, { data })
      .afterClosed()
      .subscribe((result: ConfirmDialogResult) => {
        if (result.confirm) {
          const selectedNodes: FlatTreeNode[] = deleteAll
            ? this.maintFolderService.treeControl.dataNodes
            : this.maintFolderService.treeControl.dataNodes.filter((node: FlatTreeNode) =>
                this.maintFolderService.selectionModel.isSelected(node.id)
              );
          this.maintFolderService.deleteNodes(selectedNodes).then((maintFolders: TocItemTree[]) => {
            this.updateDatasource(maintFolders);
          });
        }
      });
  }

  public hasChild(_: number, nodeData: FlatTreeNode): boolean {
    return nodeData.expandable;
  }

  /**
   * Get items from DB to populate the datasource
   */
  private initDataSource(): void {
    this.subscription.add(
      this.maintFolderService.tocItemsOfType.subscribe((data: TocItemTree[]) => {
        if (!data) {
          return this.maintFolderService.refresh();
        }
        this.updateDatasource(data);
        // Save original tree for export all
        this.originFlatTree = this.maintFolderService.treeControl.dataNodes;
        this.displayReady = true;
      })
    );
  }

  /**
   * Populate datasource with given items (maintfolders)
   * @param maintFolders tocItems used to populate datasource
   */
  private updateDatasource(maintFolders: TocItemTree[]): void {
    this.maintFolderService.sortTocItemList(maintFolders);
    this.maintFolderService.maintFolders = maintFolders;
    this.dataSource.data = maintFolders.flatMap((maintFolder: TocItemTree) =>
      this.enrichTree(maintFolder.data[0], maintFolder._id)
    );
    this.maintFolderService.reExpand();
    this.unfilteredData = this.dataSource.data;
  }

  /**
   * Add treeId and parentId in each nodes
   * That simplify the deletion nodes
   * @param node
   * @param treeId Id of the maintenance folder document
   * @param parentId Id of the parent node
   * @returns Enriched TreeNode
   */
  private enrichTree(node: TreeNode, treeId?: string, parentId?: string): TreeNode {
    return {
      ...node,
      treeId,
      parentId,
      children: node.children?.map((child: TreeNode) => this.enrichTree(child, treeId, node.id))
    };
  }

  /**
   * Search for nodes in the tree that match the query search
   * @param search value to search in the tree
   */
  private applyFilter(search: string): void {
    if (!search) {
      this.dataSource.data = this.unfilteredData;
      return;
    }

    this.dataSource.data = this.filterTree(this.unfilteredData, search);
    this.maintFolderService.treeControl.expandAll();
  }

  /**
   * Run through the tree to search for nodes matching the query. When a node matches, the node and
   * his parents are opened.
   * @param nodes tree to search value in
   * @param search value to search in the tree
   * @param parentsToExpand list of node to open
   * @returns filtered trees
   */
  private filterTree(nodes: TreeNode[], search: string): TreeNode[] {
    return nodes.reduce((acc: TreeNode[], node: TreeNode) => {
      if (node.dmc) {
        const duMeta = this.store.getDUMeta(node.dmc);
        const stringToCompare = `${duMeta.shortTitle}-${duMeta.reference}`.toLowerCase();

        const isMatch = stringToCompare.includes(search.toLowerCase());

        // Keep all children if match
        if (isMatch) {
          acc.push({ ...node });
        } else {
          // Otherwise keep filtering on children
          const filteredChildren = node.children ? this.filterTree(node.children, search) : [];

          if (filteredChildren.length > 0) {
            acc.push({
              ...node,
              children: filteredChildren
            });
          }
        }
      }
      return acc;
    }, []);
  }
}
